import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { RESTAURANT } from "@/data/menu";
import Logo from "@/components/Logo";

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/menu", label: "Menu", testid: "nav-menu" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#FBF8F3]/90 backdrop-blur-xl border-b border-[rgba(42,27,61,0.08)]" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Link to="/" data-testid="logo-link" className="flex items-center" onClick={() => setOpen(false)}>
            <Logo dark={scrolled || open} />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-[0.72rem] tracking-[0.28em] uppercase font-bold transition-colors ${
                    isActive
                      ? "text-[#E91E63]"
                      : scrolled
                      ? "text-[#2A1B3D] hover:text-[#E91E63]"
                      : "text-[#FBF8F3] hover:text-[#F5C518]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <a
            href={RESTAURANT.phoneHref}
            data-testid="navbar-phone-cta"
            className={`hidden lg:inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase font-bold border px-4 py-2.5 transition-all ${
              scrolled
                ? "border-[#2A1B3D] text-[#2A1B3D] hover:bg-[#2A1B3D] hover:text-[#FBF8F3]"
                : "border-[#FBF8F3]/70 text-[#FBF8F3] hover:bg-[#F5C518] hover:border-[#F5C518] hover:text-[#2A1B3D]"
            }`}
          >
            <Phone className="w-3.5 h-3.5" />
            {RESTAURANT.phone}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? (
              <X className={scrolled ? "text-[#2A1B3D]" : "text-[#FBF8F3]"} />
            ) : (
              <Menu className={scrolled ? "text-[#2A1B3D]" : "text-[#FBF8F3]"} />
            )}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FBF8F3] border-t border-[rgba(42,27,61,0.08)]">
          <nav className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-sm tracking-[0.28em] uppercase font-bold ${
                    isActive ? "text-[#E91E63]" : "text-[#2A1B3D]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={RESTAURANT.phoneHref}
              data-testid="mobile-phone-cta"
              className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-bold border border-[#2A1B3D] text-[#2A1B3D] px-4 py-3 mt-2"
            >
              <Phone className="w-3.5 h-3.5" /> {RESTAURANT.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
